import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useWorkspace } from '../hooks/useWorkspace';
import { createShare, listShares, revokeShare } from '../services/shareApi.js';
import {
  subscribeShareHost, getShareHostState,
  activateShare, adoptShare, setSharePaused, clearShare
} from '../services/shareHostStore.js';

const { FiShare2, FiX, FiCopy, FiCheck, FiTrash2, FiUsers, FiPause, FiPlay } = FiIcons;

const DURATIONS = [
  { hours: 1, label: '1 hour' },
  { hours: 4, label: '4 hours' },
  { hours: 24, label: '24 hours' },
  { hours: 48, label: '48 hours (max)' }
];

export default function ShareModal({ onClose }) {
  const state = useWorkspace();
  const businessId = state.business?.id;
  const companySlug = state.business?.slug;
  const businessName = state.business?.name;

  // The persistent host store owns "is a share live" — surviving modal
  // close/open and page reloads (post-launch fix: broadcast used to die with
  // the modal while the share stayed live server-side).
  const host = useSyncExternalStore(subscribeShareHost, getShareHostState);
  const activeShareId = host.shareId;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // {username, password} — shown once
  const [activeShares, setActiveShares] = useState([]);
  const [copied, setCopied] = useState(null);
  const [viewers, setViewers] = useState(0);

  const refreshList = useCallback(async () => {
    if (!businessId) return;
    try {
      const result = await listShares(businessId);
      setActiveShares(result.shares || []);
      const live = (result.shares || []).find((share) => share.id === activeShareId);
      setViewers(live ? (live.activeViewers || 0) : 0);
      // Auto-adopt: a live share exists server-side but this tab isn't
      // broadcasting (page reload mid-share, or the modal was reopened after
      // a refresh). Re-arms the broadcast loop instead of mirroring nothing.
      if (!activeShareId) {
        const adoptable = (result.shares || []).find((share) => share.live);
        if (adoptable) {
          adoptShare(adoptable.id, { businessName, expiresAt: adoptable.expiresAt });
          setCreated((prev) => prev || { username: adoptable.username });
        }
      }
    } catch { /* non-fatal: list stays stale */ }
  }, [businessId, activeShareId, businessName]);

  useEffect(() => {
    refreshList();
    return undefined;
  }, [refreshList]);

  // Light poll while a share is live: keeps "N viewers watching" honest
  // without a second SSE channel on the host side.
  useEffect(() => {
    if (!activeShareId) return undefined;
    const poll = setInterval(refreshList, 10_000);
    return () => clearInterval(poll);
  }, [activeShareId, refreshList]);

  // The server ended the share under us (revoked elsewhere / expired) —
  // tear down so the modal returns to the create view.
  useEffect(() => {
    if (host.lastError === 'revoked' || host.lastError === 'expired') clearShare();
  }, [host.lastError]);

  async function handleCreate(durationHours) {
    setBusy(true);
    setError(null);
    try {
      const segments = window.location.pathname.split('/').filter(Boolean); // [workspace, company, screen?]
      const share = await createShare({
        businessId,
        screenSlug: segments.length >= 3 ? segments[segments.length - 1] : 'headquarters',
        durationHours
      });
      setCreated({ username: share.username, password: share.password });
      setViewers(0);
      // Starts the persistent broadcast loop immediately (first frame goes out
      // now — viewers never see "Waiting…" from a host who is already live).
      activateShare({ shareId: share.id, businessName, expiresAt: share.expiresAt });
      refreshList();
    } catch (err) {
      setError(err.message || 'Could not create the share.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(shareId) {
    setBusy(true);
    try {
      await revokeShare(shareId);
      if (shareId === activeShareId) {
        setCreated(null);
        setViewers(0);
        clearShare();
      }
      refreshList();
    } catch (err) {
      setError(err.message || 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  }

  function copyText(key, text) {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    }).catch(() => {});
  }

  function copyAll() {
    if (!created) return;
    const link = `${window.location.origin}/workspace/${companySlug || 'workspace'}`;
    copyText('all', `Join my live share:\n${link}\nUsername: ${created.username}\nPassword: ${created.password}`);
  }

  const shareUrl = `${window.location.origin}/workspace/${companySlug || 'workspace'}`;

  return (
    <div className="add-page-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="add-page-dialog share-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Share workspace live"
      >
        <div className="section-heading">
          <div className="inline">
            <SafeIcon icon={FiShare2} />
            <h3>Share workspace live</h3>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <p className="form-hint">
          You are the HOST. Viewers see only what you broadcast — a live mirror, never access to
          your data. Duration is capped at 48 hours. Sharing keeps running while this tab is open,
          even with this panel closed.
        </p>

        {error && <div className="form-error">{error}</div>}

        {!activeShareId ? (
          <div className="share-duration-row">
            <span className="eyebrow">Share duration</span>
            <div className="share-duration-options">
              {DURATIONS.map(({ hours, label }) => (
                <button
                  key={hours}
                  type="button"
                  className="button secondary compact"
                  disabled={busy || !businessId}
                  onClick={() => handleCreate(hours)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="share-active-box">
            <div className="inline spread">
              <span className="inline">
                <span className={`share-live-dot ${host.paused ? 'paused' : ''}`} aria-hidden="true" />
                <b>{host.paused ? 'Paused' : 'Live'}</b>
                <span className="share-viewer-count">
                  {host.paused
                    ? 'broadcast paused'
                    : `${Math.max(viewers, host.lastDelivered)} viewer${Math.max(viewers, host.lastDelivered) === 1 ? '' : 's'} watching`}
                </span>
              </span>
              <span className="inline">
                <button
                  type="button"
                  className="button ghost compact share-revoke"
                  onClick={() => setSharePaused(!host.paused)}
                  disabled={busy}
                  title={host.paused ? 'Resume broadcasting' : 'Freeze the mirror for viewers (credentials stay valid)'}
                >
                  <SafeIcon icon={host.paused ? FiPlay : FiPause} />
                  {host.paused ? 'Resume' : 'Pause'}
                </button>
                <button
                  type="button"
                  className="button ghost compact share-revoke"
                  onClick={() => handleRevoke(activeShareId)}
                  disabled={busy}
                >
                  <SafeIcon icon={FiTrash2} /> Stop sharing
                </button>
              </span>
            </div>

            {host.lastError && host.lastError !== 'revoked' && host.lastError !== 'expired' && (
              <small className="form-error">Last broadcast attempt failed: {host.lastError} — retrying on the next frame.</small>
            )}

            <div className="share-cred-grid">
              <label>Link
                <span className="inline">
                  <input readOnly value={shareUrl} />
                  <button type="button" className="icon-button" onClick={() => copyText('link', shareUrl)} aria-label="Copy link">
                    <SafeIcon icon={copied === 'link' ? FiCheck : FiCopy} />
                  </button>
                </span>
              </label>
              <label>Username
                <span className="inline">
                  <input readOnly value={activeShares.find((s) => s.id === activeShareId)?.username || created?.username || activeShareId} />
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => copyText('user', activeShares.find((s) => s.id === activeShareId)?.username || created?.username || '')}
                    aria-label="Copy username"
                  >
                    <SafeIcon icon={copied === 'user' ? FiCheck : FiCopy} />
                  </button>
                </span>
              </label>
              <label>Password
                {created?.password ? (
                  <span className="inline">
                    <input readOnly value={created.password} />
                    <button type="button" className="icon-button" onClick={() => copyText('pass', created.password)} aria-label="Copy password">
                      <SafeIcon icon={copied === 'pass' ? FiCheck : FiCopy} />
                    </button>
                  </span>
                ) : (
                  <small className="muted">Shown only at creation. Revoke and recreate to rotate it.</small>
                )}
              </label>
            </div>

            {created?.password && (
              <button type="button" className="button primary compact" onClick={copyAll}>
                {copied === 'all' ? <SafeIcon icon={FiCheck} /> : <SafeIcon icon={FiCopy} />}
                {copied === 'all' ? 'Copied' : 'Copy all credentials'}
              </button>
            )}
          </div>
        )}

        <div className="share-list-section">
          <span className="eyebrow">Active shares</span>
          {activeShares.length === 0 ? (
            <p className="muted share-empty">No shares yet.</p>
          ) : (
            <div className="share-list">
              {activeShares.map((share) => (
                <div key={share.id} className="share-row">
                  <span className="inline">
                    <SafeIcon icon={FiUsers} />
                    <b>{share.username}</b>
                    <span className="result-status-chip">
                      {share.id === activeShareId && !host.paused ? 'live'
                        : (share.id === activeShareId ? 'paused' : (share.live ? 'live' : (share.revoked ? 'revoked' : 'expired')))}
                    </span>
                  </span>
                  <span className="inline share-row-meta">
                    <small className="muted">{share.screenSlug} · {share.activeViewers} viewing · ends {String(share.expiresAt).slice(0, 10)}</small>
                    <button type="button" className="icon-button share-revoke" onClick={() => handleRevoke(share.id)} aria-label={`Revoke ${share.username}`}>
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
